import React from "react";
import Panel from "../../components/ui/Panel/Panel.jsx";
import PageHeader from "../../components/marketing/PageHeader.jsx";
import SeoHead from "../../seo/SeoHead.jsx";
import { ROUTE_META } from "../../seo/siteMeta.js";

// Ported from form720-ui/src/Pages/legal/TermsOfServicePage.jsx and adapted for
// Form 2290. Same company (Quantiva LLC), same mailing address, same substance;
// what changed is the product name, the form and its title, the support address,
// and the description of who the Service is for.
//
// NOTE FOR THE OWNER — carried over from the 720 version and still true here:
// - These are general SaaS terms for a US audience. Have counsel review before
//   relying on them as binding, especially the liability and indemnity clauses.
const SECTIONS = [
  {
    title: "1. Acceptance of terms",
    body:
      'By accessing or using EasyFile2290 (the "Service"), a product of Quantiva LLC ("Quantiva," "we," "us," or ' +
      '"our"), you agree to be bound by these Terms of Service and our Privacy Policy. If you are using the ' +
      "Service on behalf of a business, you represent that you are authorized to bind that business to these " +
      "Terms. If you do not agree, do not use the Service.",
  },
  {
    title: "2. The Service",
    body:
      "EasyFile2290 is an IRS-authorized e-file provider for Form 2290, the Heavy Highway Vehicle Use Tax Return, " +
      "and related forms and schedules, including Schedule 1. We provide software that helps you prepare your " +
      "return and electronically transmit it to the IRS e-file system as you direct. We are not a law firm or " +
      "accounting firm, and the Service does not constitute legal, tax, or accounting advice.",
  },
  {
    title: "3. Eligibility",
    body:
      "You must be at least 18 years old and capable of forming a binding contract to use the Service. The " +
      "Service is intended for businesses and individuals filing United States federal highway use tax returns " +
      "for heavy vehicles.",
  },
  {
    title: "4. Your account and security",
    body:
      "You are responsible for maintaining the confidentiality of your login credentials and for all activity " +
      "that occurs under your account. You agree to provide accurate account information and to notify us " +
      "immediately at support@easyfile2290.com of any unauthorized use or suspected breach of your account.",
  },
  {
    title: "5. Your responsibilities and accuracy of filings",
    body:
      "You are responsible for the accuracy and completeness of the information you submit — including each " +
      "vehicle identification number, taxable gross weight category, and first-used month — and for filing on " +
      "time. You can review your return before it is filed. Because the IRS assesses penalties and interest based " +
      "on what is filed and when, we are not responsible for IRS penalties, interest, or other consequences " +
      "resulting from errors or omissions in the information you provide or from returns filed late.",
  },
  {
    title: "6. Fees and payment",
    body:
      "The fees for the Service are presented to you before you authorize a filing or purchase. By submitting " +
      "payment, you authorize us and our payment processor, Stripe, to charge the applicable amount to your " +
      "payment method. You represent that you are authorized to use the payment method provided. Fees are stated " +
      "in U.S. dollars and are exclusive of any taxes you may owe. If a payment fails, is reversed, or is " +
      "otherwise left unpaid, we may suspend your access to the Service and take reasonable steps to collect the " +
      "amount owed.",
  },
  {
    title: "7. Refunds",
    body:
      "Once we submit your return to the IRS, the filing you paid for is complete, so the service fee for that " +
      "filing is final and cannot be refunded, except where the law requires otherwise. Any tax you owe is paid " +
      "to the IRS, not to us, so we are not able to refund it.",
  },
  {
    title: "8. Referral credits",
    body:
      "You may share your personal referral link. When someone creates an account through your link and their " +
      "first return is accepted by the IRS and paid for, you earn a referral credit equal to the service fee for " +
      "an original return with one vehicle on the day it is earned. Credits are taken off the service fee of your " +
      "own original or amended returns when you pay per return (not when you use a subscription credit); " +
      "several credits can be used on one return, up to that " +
      "return's service fee. You can earn at most 10 credits in each July–June tax period. A credit can be used " +
      "until June 30 at the end of the next tax period. Credits have no cash value, cannot be transferred, and " +
      "are not paid out. No credit is earned for a return filed under an employer identification number already " +
      "on your account, or for a business that already had a return accepted with us under another account. A " +
      "credit is cancelled if the payment that earned it is refunded, and any part of it not yet used can no " +
      "longer be used. When you share your link, you must say that you receive a credit if people file through it. We " +
      "never tell you who signed up or what they filed, only how many credits you have earned. We may change or " +
      "end the referral program at any time; credits already earned stay usable until they expire.",
  },
  {
    title: "9. Acceptable use",
    body:
      "You agree not to misuse the Service, including by: submitting false or fraudulent information; filing on " +
      "behalf of a party without authorization; attempting to gain unauthorized access to the Service or its " +
      "data; interfering with or disrupting the Service; or using the Service in violation of any applicable law.",
  },
  {
    title: "10. Intellectual property",
    body:
      "All content, software, design, and trademarks on EasyFile2290 are the property of Quantiva LLC or its " +
      "licensors and are protected by intellectual property laws. We grant you a limited, non-exclusive, " +
      "non-transferable right to use the Service for its intended purpose. You retain ownership of the data you " +
      "submit, and you grant us the rights necessary to process and transmit it to provide the Service.",
  },
  {
    title: "11. Third-party services",
    body:
      "The Service relies on third parties, including the IRS e-file system and our payment processor, Stripe. " +
      "Your use of those services may be subject to their own terms. We are not responsible for the " +
      "availability, acts, or omissions of third-party services or the IRS.",
  },
  {
    title: "12. Disclaimer of warranties",
    body:
      'The Service is provided "as is" and "as available," without warranties of any kind, whether express or ' +
      "implied, including warranties of merchantability, fitness for a particular purpose, and non-infringement. " +
      "We do not warrant that the Service will be uninterrupted, error-free, or that any filing will be accepted " +
      "by the IRS.",
  },
  {
    title: "13. Limitation of liability",
    body:
      "To the maximum extent permitted by law, Quantiva LLC, EasyFile2290, and our suppliers and service " +
      "providers will not be liable for any indirect, incidental, special, exemplary, consequential, or punitive " +
      "damages, or for any loss of profits, revenue, business, goodwill, or data — whether based in contract, " +
      "tort (including negligence), strict liability, or any other theory, and even if we have been advised of " +
      "the possibility of such damages or a limited remedy fails of its essential purpose. We do not audit, " +
      "verify, or independently confirm the information you provide, and we are not responsible for disallowed " +
      "claims or credits. Without limiting the above, we are not liable for: IRS penalties, interest, or " +
      "additional taxes resulting from inaccurate, incomplete, misleading, or untimely information you provide; " +
      "the tax positions you take; your inability to file or any delay in filing; loss, corruption, or " +
      "unauthorized access to data; service interruptions, telecommunication or internet failures, or viruses or " +
      "other harmful code; or any access to your account through your credentials by another person. We are not " +
      "responsible for third-party services or websites, and we assume no liability to anyone other than you. " +
      "Our total cumulative liability for any claim arising out of or relating to the Service will not exceed the " +
      "total fees you paid us for the Service in the twelve months before the event giving rise to the claim. " +
      "These limitations are a fundamental part of the agreement between you and us.",
  },
  {
    title: "14. Indemnification",
    body:
      "You agree to indemnify and hold harmless Quantiva LLC and its officers, employees, and agents from any " +
      "claims, losses, liabilities, and expenses (including reasonable attorneys’ fees) arising from your use of " +
      "the Service, your submitted information, or your violation of these Terms or applicable law.",
  },
  {
    title: "15. Termination",
    body:
      "We may suspend or terminate your access to the Service for violations of these Terms or applicable law, or " +
      "to protect the Service or its users. You may stop using the Service or close your account at any time. " +
      "Provisions that by their nature should survive termination — including Sections 9, 11, 12, 13, and 15 — " +
      "will survive.",
  },
  {
    title: "16. Governing law and disputes",
    body:
      "These Terms are governed by the laws of the State of Texas, without regard to its conflict-of-law " +
      "principles. You agree that any dispute arising out of or relating to these Terms or the Service will be " +
      "resolved exclusively in the state or federal courts located in Texas, and you consent to their personal " +
      "jurisdiction.",
  },
  {
    title: "17. Changes to these terms",
    body:
      'We may update these Terms from time to time. The "Last updated" date above reflects the most recent ' +
      "revision. Material changes will be communicated via email or in-app notification. Your continued use of " +
      "the Service after an update means you accept the revised Terms.",
  },
  {
    title: "18. Contact us",
    body:
      "Questions about these Terms can be sent to support@easyfile2290.com, or by mail to: Quantiva LLC, 2591 " +
      "Dallas Parkway, Suite 300 PMB 231, Frisco, TX 75034.",
  },
];

const meta = ROUTE_META["/terms"];

const Terms = () => (
  <div className="max-w-[820px] mx-auto px-[22px] py-[46px] pb-[70px]">
    <SeoHead title={meta.title} description={meta.description} path="/terms" keywords={meta.keywords} />

    <PageHeader eyebrow="Legal" title="Terms of Service">
      Last updated: October 9, 2026
    </PageHeader>

    <Panel padded className="mt-8 !p-[30px] text-[15px] leading-[1.7] text-[var(--ink)]">
      {SECTIONS.map((s) => (
        <section key={s.title} className="mb-7 last:mb-0">
          <h2 className="font-disp font-bold text-[17px] tracking-[-.01em] mb-2">{s.title}</h2>
          <p className="text-[var(--muted)]">{s.body}</p>
        </section>
      ))}
    </Panel>
  </div>
);

export default Terms;
